import { Operation } from "rfc6902/diff";

import {
	createBlueprint,
	CreateBlueprintInfo,
	getBlueprintContent,
	getBlueprintEndpoint,
	updateBlueprintContent
} from "core/blueprint/base/BlueprintService";
import { Competence } from "core/blueprint/competencelist/CompetenceListService";
import { Field, RatingInfo } from "core/field/FieldService";
import { ScoreResolution } from "evaluation/ScoreResolution";
import { Widget } from "../dashboard/DashboardTypes";

const TRAJECTORY_BLUEPRINT_ENDPOINT = "trajectories";
const ROLES_SUFFIX = "roles";
const USERS_SUFFIX = "users";

export type Report = {
	id: number;
	version: number;
	name: string;
	fields: Field[];
};

export enum InformationRight {
	WRITE,
	READ,
	HIDDEN
}

export type InformationRights = {
	// Key is the uuid of the field, or an empty string for all.
	[key: string]: boolean[];
};

export enum ReportRight {
	ADMIN,
	WRITE,
	READ,
	HIDDEN
}

export type ReportFieldRights = {
	[key: string]: boolean[];
};

export type ReportRights = {
	// Key is the name of the report as named within the trajectory.
	[key: string]: ReportFieldRights;
};

export enum EvaluationRight {
	WRITE,
	READ
}

export enum AdminRight {
	PURCHASE,
	METADATA,
	RIGHTS
}

export type Role = {
	name: string;

	evaluationRights: boolean[];
	informationRights: InformationRights;
	reportRights: ReportRights;
	adminRights: boolean[];
};

export type UserRights = {
	user: string;
	roles: string[];
};

export type CompetenceList = {
	id: number;
	version: number;
	competences: Competence[];
};

export type Dashboard = {
	id: number;
	version: number;
	widgets: Array<Widget>;
};

export type Information = {
	id: number;
	version: number;
	fields: Array<Field>;
};

export type TrajectoryBlueprint = {
	dashboard: Dashboard | null;
	information: Information | null;
	competenceList: CompetenceList | null;
	scoreInfo: RatingInfo[];
	scoreResolution: ScoreResolution;
	reportBlueprints: Report[];
	roles: Role[];
	users: UserRights[];
};

export function createTrajectoryBlueprint(
	info: CreateBlueprintInfo
): Promise<string> {
	return createBlueprint(TRAJECTORY_BLUEPRINT_ENDPOINT, info);
}

export function getTrajectoryBlueprintContent(
	id: number
): Promise<TrajectoryBlueprint> {
	return getBlueprintContent<TrajectoryBlueprint>(
		TRAJECTORY_BLUEPRINT_ENDPOINT,
		id
	);
}

export function updateTrajectoryBlueprintContent(
	id: number,
	content: Operation[]
): Promise<void> {
	return updateBlueprintContent(TRAJECTORY_BLUEPRINT_ENDPOINT, id, content);
}

export function getTrajectoryBlueprintRoles(id: number): Promise<Role[]> {
	return getBlueprintEndpoint(
		`${TRAJECTORY_BLUEPRINT_ENDPOINT}/${id}/${ROLES_SUFFIX}`
	);
}

export function getTrajectoryBlueprintUsers(id: number): Promise<UserRights[]> {
	return getBlueprintEndpoint(
		`${TRAJECTORY_BLUEPRINT_ENDPOINT}/${id}/${USERS_SUFFIX}`
	);
}
